import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';

import { Dialog } from 'primereact/dialog';
import { InputTextarea } from 'primereact/inputtextarea';

import FormTributos from '../components/FormTributos';
import {
  obterRecebimento, conferirRecebimento, conferirProduto, ignorarRecebimento, pesquisarRecebimentos, mensagemErro,
} from '../tributosApi';
import {
  StatusTag, SituacaoProdutoTag, formatarCnpj, formatarNcm, normalizarTributos,
} from '../util';

function Info({ label, children }) {
  return (
    <div className="tb-info">
      <small>{label}</small>
      <span>{children || '—'}</span>
    </div>
  );
}

Info.propTypes = { label: PropTypes.string.isRequired, children: PropTypes.node };
Info.defaultProps = { children: null };

export default function Conferencia({ onAlteracao }) {
  const { id } = useParams();
  const navigate = useNavigate();

  const [dados, setDados] = useState(null);
  const [form, setForm] = useState(normalizarTributos(null));
  const [salvando, setSalvando] = useState(false);
  const [dialogIgnorar, setDialogIgnorar] = useState(false);
  const [observacao, setObservacao] = useState('');

  const carregar = useCallback(async () => {
    setDados(null);
    try {
      const d = await obterRecebimento(id);
      const r = d.recebimento;
      const inicial = normalizarTributos(d.tributos);
      if (!inicial.codigoEan) inicial.codigoEan = r.codigoEan;
      if (!inicial.descricao) inicial.descricao = r.descricao;
      if (!inicial.codigoEan && !inicial.cnpjCliente) inicial.cnpjCliente = r.cnpjEmpresa || '';
      if (!inicial.codigoEan && !inicial.registroProduto) inicial.registroProduto = r.registroProduto || null;
      if (!inicial.ncm && r.ncm) inicial.ncm = r.ncm;
      if (!inicial.cest && r.cest) inicial.cest = r.cest;
      setForm(inicial);
      setDados(d);
    } catch (e) {
      toast.error(mensagemErro(e, 'Recebimento não encontrado.'));
      navigate('/tributos/recebimentos');
    }
  }, [id, navigate]);

  useEffect(() => {
    setObservacao('');
    carregar();
  }, [carregar]);

  const irParaProximo = async () => {
    try {
      const pendentes = await pesquisarRecebimentos({ status: 'PENDENTE' });
      const proximo = pendentes.find(p => String(p.id) !== String(id));
      if (proximo) {
        navigate(`/tributos/conferencia/${proximo.id}`);
        return;
      }
      toast.success('Fila de conferência concluída!');
    } catch (e) {
      // segue para a lista
    }
    navigate('/tributos/recebimentos');
  };

  const conferir = async (proximo = false) => {
    if (salvando || !dados) return;
    setSalvando(true);
    try {
      const pendente = dados.recebimento.status === 'PENDENTE';
      if (pendente || !form.id) {
        await conferirRecebimento(id, form, observacao);
      } else {
        await conferirProduto(form.id, form);
      }
      toast.success('Tributação conferida com sucesso.');
      onAlteracao();
      if (proximo) {
        await irParaProximo();
      } else {
        await carregar();
      }
    } catch (e) {
      toast.error(mensagemErro(e, 'Erro ao conferir o produto.'));
    } finally {
      setSalvando(false);
    }
  };

  const ignorar = async () => {
    setSalvando(true);
    try {
      await ignorarRecebimento(id, observacao);
      toast.info('Recebimento ignorado.');
      setDialogIgnorar(false);
      onAlteracao();
      await irParaProximo();
    } catch (e) {
      toast.error(mensagemErro(e));
    } finally {
      setSalvando(false);
    }
  };

  useEffect(() => {
    const atalho = e => {
      if (e.ctrlKey && e.key === 'Enter') {
        e.preventDefault();
        conferir(true);
      }
    };
    window.addEventListener('keydown', atalho);
    return () => window.removeEventListener('keydown', atalho);
  });

  if (!dados) {
    return <div className="tb-page"><div className="tb-empty"><i className="pi pi-spin pi-spinner" /> Carregando…</div></div>;
  }

  const r = dados.recebimento;
  const pendente = r.status === 'PENDENTE';
  const outros = (dados.historico || []).filter(h => h.id !== r.id);

  return (
    <div className="tb-page">
      <div className="tb-page-head">
        <div>
          <button type="button" className="tb-btn tb-btn-link tb-back" onClick={() => navigate('/tributos/recebimentos')}>
            <i className="pi pi-arrow-left" /> Recebimentos
          </button>
          <h1>Conferência fiscal</h1>
          <p className="tb-head-meta">
            <StatusTag status={r.status} />
            {dados.tributos ? <SituacaoProdutoTag produto={dados.tributos} /> : null}
            {dados.tributos && dados.tributos.dataConferencia ? <span>Última conferência: {dados.tributos.dataConferencia}</span> : null}
          </p>
        </div>
      </div>

      <div className="tb-split">
        <aside className="tb-card tb-side-info">
          <h2>Produto recebido</h2>
          <div className="tb-ean-big">{r.codigoEan || <span className="tb-sem-ean">Sem EAN</span>}</div>
          <p className="tb-desc-big">{r.descricao}</p>

          <h3>Dados da nota fiscal</h3>
          <div className="tb-info-grid">
            <Info label="NCM">{r.ncm ? formatarNcm(r.ncm) : null}</Info>
            <Info label="CEST">{r.cest}</Info>
            <Info label="CFOP">{r.cfop}</Info>
            <Info label="CST/CSOSN">{r.cst}</Info>
            <Info label="Número NF">{r.numeroNotaFiscal}</Info>
            <Info label="Fornecedor">{r.nomeFornecedor || formatarCnpj(r.cnpjFornecedor)}</Info>
          </div>

          <h3>Origem</h3>
          <div className="tb-info-grid">
            <Info label="Cliente">{r.nomeEmpresa}</Info>
            <Info label="CNPJ">{formatarCnpj(r.cnpjEmpresa)}</Info>
            <Info label="Registro do produto">{r.registroProduto}</Info>
            <Info label="Recebido em">{r.dataHoraRecebimento}</Info>
            <Info label="Envios">{r.quantidadeEnvios}</Info>
          </div>

          {r.dataHoraConferencia ? (
            <>
              <h3>{r.status === 'IGNORADO' ? 'Ignorado' : 'Conferência'}</h3>
              <div className="tb-info-grid">
                <Info label="Data">{r.dataHoraConferencia}</Info>
                <Info label="Operador">{r.usuarioConferencia}</Info>
              </div>
              {r.observacao ? <p className="tb-obs">{r.observacao}</p> : null}
            </>
          ) : null}

          {outros.length ? (
            <>
              <h3>Histórico deste EAN</h3>
              <ul className="tb-history">
                {outros.slice(0, 8).map(h => (
                  <li key={h.id}>
                    <StatusTag status={h.status} />
                    <span>{h.dataHoraUltimoEnvio || h.dataHoraRecebimento}</span>
                    <small>{h.nomeEmpresa || formatarCnpj(h.cnpjEmpresa)}</small>
                  </li>
                ))}
              </ul>
            </>
          ) : null}
        </aside>

        <div className="tb-card tb-form-card">
          <FormTributos valor={form} onChange={setForm} bloquearEan referenciaNota={{ ncm: r.ncm, cest: r.cest }} />

          {pendente ? (
            <div className="tb-field tb-span-12 tb-obs-field">
              <label htmlFor="obs">Observação (opcional)</label>
              <InputTextarea id="obs" value={observacao} onChange={e => setObservacao(e.target.value)} rows={2} autoResize maxLength={500} />
            </div>
          ) : null}

          <div className="tb-actions">
            {pendente ? (
              <button type="button" className="tb-btn tb-btn-danger-ghost" onClick={() => setDialogIgnorar(true)} disabled={salvando}>
                <i className="pi pi-ban" /> Ignorar
              </button>
            ) : <span />}
            <div className="tb-actions-right">
              <button type="button" className="tb-btn tb-btn-ghost" onClick={() => conferir(false)} disabled={salvando}>
                <i className="pi pi-check" /> {pendente ? 'Conferir' : 'Salvar e reconferir'}
              </button>
              {pendente ? (
                <button type="button" className="tb-btn tb-btn-primary" onClick={() => conferir(true)} disabled={salvando} title="Ctrl + Enter">
                  {salvando ? <i className="pi pi-spin pi-spinner" /> : <i className="pi pi-check-circle" />} Conferir e próximo
                </button>
              ) : null}
            </div>
          </div>
        </div>
      </div>

      <Dialog
        className="tb-dialog"
        header="Ignorar recebimento"
        visible={dialogIgnorar}
        onHide={() => setDialogIgnorar(false)}
        style={{ width: '28rem', maxWidth: '95vw' }}
        footer={(
          <div>
            <button type="button" className="tb-btn tb-btn-ghost" onClick={() => setDialogIgnorar(false)}>Cancelar</button>
            <button type="button" className="tb-btn tb-btn-danger" onClick={ignorar} disabled={salvando}>Ignorar</button>
          </div>
        )}
      >
        <p>O produto sairá da fila sem ter a tributação conferida. Informe o motivo:</p>
        <InputTextarea value={observacao} onChange={e => setObservacao(e.target.value)} rows={3} autoResize style={{ width: '100%' }} maxLength={500} />
      </Dialog>
    </div>
  );
}

Conferencia.propTypes = {
  onAlteracao: PropTypes.func.isRequired,
};
